import React, { useEffect, useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Home, FileText, Users, User, Settings, HelpCircle, Building2, CreditCard, ArrowRightLeft, Lock, FileBarChart, BarChart3 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { APP_VERSION } from '../../lib/version';
import { getNotificationCounts } from '../../services/offices';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const navigation = [
  { name: 'Home', to: '/app', icon: Home, requiresAccess: true },
  { name: 'Notes', to: '/app/notes', icon: FileText, requiresAccess: true },
  { name: '90 Day Review', to: '/app/90-day-eval', icon: FileBarChart, requiresAccess: true },
  { name: 'Clients', to: '/app/clients', icon: Users, requiresAccess: true },
  { name: 'Offices', to: '/app/offices', icon: Building2, requiresAccess: true },
  { name: 'Transfers', to: '/app/transfers', icon: ArrowRightLeft, requiresAccess: true },
  { name: 'Profile', to: '/app/profile', icon: User, requiresAccess: false },
  { name: 'Team Stats', to: '/app/team-stats', icon: BarChart3, requiresAccess: true, orgAdminOnly: true },
  { name: 'Billing', to: '/app/billing', icon: CreditCard, requiresAccess: false },
  { name: 'Help', to: '/app/help', icon: HelpCircle, requiresAccess: false },
  { name: 'Settings', to: '/app/settings', icon: Settings, requiresAccess: false },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user, accessResult, accessInfo, checkingAccess } = useAuth();
  const isOrgAdmin = accessInfo?.is_org_admin === true;
  const navigate = useNavigate();
  const [notificationCounts, setNotificationCounts] = useState({ office: 0, transfer: 0, total: 0 });

  useEffect(() => {
    let isMounted = true;

    async function loadNotificationCounts() {
      if (!user?.id) return;

      try {
        const counts = await getNotificationCounts();
        if (isMounted) {
          setNotificationCounts(counts);
        }
      } catch (error) {
        console.error('[Sidebar] Error loading notification counts:', error);
        // Don't update state on error to keep last known good state
      }
    }

    // PERFORMANCE FIX: Defer notification counts to avoid connection pool exhaustion
    // Allow critical queries to complete first
    const timer = setTimeout(() => {
      loadNotificationCounts();
    }, 1000); // Defer by 1 second

    // Set up real-time subscriptions for automatic updates
    if (!user?.id) return;

    const transferChannel = supabase
      .channel('transfer-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_transfer_requests',
          filter: `to_user_id=eq.${user.id}`
        },
        () => {
          if (isMounted) {
            loadNotificationCounts();
          }
        }
      )
      .subscribe();

    const officeChannel = supabase
      .channel('office-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'office_invites'
        },
        () => {
          if (isMounted) {
            loadNotificationCounts();
          }
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      clearTimeout(timer);
      supabase.removeChannel(transferChannel);
      supabase.removeChannel(officeChannel);
    };
  }, [user?.id]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-gray-600 bg-opacity-75 z-30"
          onClick={onClose}
        />
      )}

      {/* Sidebar content */}
      <div
        className={cn(
          "fixed lg:sticky top-0 lg:top-16 left-0 z-40 bg-white transform transition-transform duration-200 ease-in-out",
          "lg:translate-x-0 border-r border-gray-200 h-[calc(100vh-4rem)] w-64",
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <nav className="h-full flex flex-col p-4 overflow-y-auto bg-white">
          <div className="flex-1 space-y-1">
            {navigation.filter((item) => !item.orgAdminOnly || isOrgAdmin).map((item) => {
              const Icon = item.icon;
              const showBadge =
                (item.name === 'Offices' && notificationCounts.office > 0) ||
                (item.name === 'Transfers' && notificationCounts.transfer > 0);
              const badgeCount =
                item.name === 'Offices' ? notificationCounts.office :
                item.name === 'Transfers' ? notificationCounts.transfer : 0;

              // Check if this item is locked (requires access but user doesn't have it)
              const isLocked = !checkingAccess && item.requiresAccess && !accessResult?.hasAccess;

              // If locked, render a disabled button instead of a link
              if (isLocked) {
                return (
                  <button
                    key={item.name}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/app/paywall');
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors touch-target text-gray-400 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center">
                      <Lock className="w-5 h-5 mr-3" />
                      {item.name}
                    </div>
                    {showBadge && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-gray-300 text-white text-xs font-semibold rounded-full">
                        {badgeCount > 9 ? '9+' : badgeCount}
                      </span>
                    )}
                  </button>
                );
              }

              return (
                <NavLink
                  key={item.name}
                  to={item.to}
                  end={item.to === '/app'}
                  onClick={onClose}
                  className={({ isActive }) => cn(
                    "flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors touch-target",
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </div>
                  {showBadge && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-semibold rounded-full">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>

          <div className="pt-4 mt-4 border-t border-gray-200">
            <div className="flex flex-col gap-2 text-xs text-gray-500">
              <Link
                to="/terms"
                onClick={onClose}
                className="hover:text-gray-700 transition-colors px-4 py-1"
              >
                Terms of Service
              </Link>
              <Link
                to="/privacy"
                onClick={onClose}
                className="hover:text-gray-700 transition-colors px-4 py-1"
              >
                Privacy Policy
              </Link>
              <p className="px-4 py-1 text-gray-400">
                Version {APP_VERSION}
              </p>
              <p className="px-4 py-1 text-gray-400">
                &copy; {new Date().getFullYear()} Sano
              </p>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}